import React from "react";
import { Link } from "react-router-dom";
import { useT, useApp } from "@/context/AppContext";
import { usePartner } from "@/context/PartnerContext";

const FOOTER_I18N = {
  nl: { product: "Product", account: "Account", desc: "Het onafhankelijke technologie-platform voor de recreatiebranche. Van bookingszuilen tot digitale sloten, ANPR, AI-camera's en IoT, alle fysieke en operationele tech, naadloos gekoppeld." },
  en: { product: "Product", account: "Account", desc: "The independent technology platform for the leisure industry. From booking kiosks to smart locks, ANPR, AI cameras and IoT, all physical and operational tech, seamlessly connected." },
  de: { product: "Produkt", account: "Konto", desc: "Die unabhängige Technologieplattform für die Freizeitbranche. Von Buchungskiosken über smarte Schlösser, ANPR, KI-Kameras bis IoT, alle physische und operative Technik, nahtlos verbunden." },
  fr: { product: "Produit", account: "Compte", desc: "La plateforme technologique indépendante pour le secteur des loisirs. Des bornes de réservation aux serrures connectées, ANPR, caméras IA et IoT, toute la tech physique et opérationnelle, connectée." },
  es: { product: "Producto", account: "Cuenta", desc: "La plataforma tecnológica independiente para el sector del ocio. Desde kioscos de reserva hasta cerraduras inteligentes, ANPR, cámaras con IA e IoT, toda la tecnología física y operativa, conectada." },
  it: { product: "Prodotto", account: "Account", desc: "La piattaforma tecnologica indipendente per il settore del tempo libero. Da chioschi di prenotazione a serrature smart, ANPR, telecamere AI e IoT, tutta la tecnologia fisica e operativa, connessa." },
  da: { product: "Produkt", account: "Konto", desc: "Den uafhængige teknologiplatform til fritidsbranchen. Fra bookingkiosker til smarte låse, ANPR, AI-kameraer og IoT, al fysisk og operationel teknologi, sømløst forbundet." },
};

export default function Footer() {
  const t = useT();
  const { lang } = useApp();
  const f = FOOTER_I18N[lang] || FOOTER_I18N.en;
  const { partner, prefix, isPartner } = usePartner();
  const [logoFailed, setLogoFailed] = React.useState(false);
  React.useEffect(() => { setLogoFailed(false); }, [partner?.slug]);

  // Default brand for the platform is RECRA Solutions (matches Header + douchelezer-style brand).
  const brand = partner?.footer_brand || partner?.name || "RECRA Solutions";
  const subtitle = partner ? "Configurator" : "Partner Platform";

  // Default description. Partner mode uses their tagline + generic hardware copy.
  const desc = isPartner
    ? (partner?.tagline_nl ||
       `${partner?.name} kiosk programma, kies hardware, koppel je software en bestel in jouw huisstijl.`)
    : f.desc;

  // RECRA palette, dark olive on cream
  const olive = "#3D3B00";
  const oliveSoft = "#4F4D2A";
  const oliveMuted = "#7A7860";
  const accent = partner?.primary_color || "#39C46F";

  return (
    <footer className="mt-24 border-t" style={{ background: "#FBF7EC", borderColor: "#E5DFC8" }} data-testid="app-footer">
      <div className="container-x py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-3">
            {!isPartner ? (
              <img src="/brand/recra-logo-dark.png" alt="RECRA Solutions" className="h-9 object-contain" data-testid="footer-brand" />
            ) : (
              <>
                {partner?.logo_url && !logoFailed ? (
                  <div
                    className="h-10 px-2.5 rounded-md flex items-center"
                    style={{
                      background: partner.logo_on_dark ? "#3D3B00" : "#FFFFFF",
                      border: "1px solid #E5DFC8",
                    }}
                  >
                    <img
                      src={partner.logo_url}
                      alt={partner.name}
                      className="h-6 max-w-[150px] object-contain"
                      data-testid="footer-partner-logo"
                      onError={() => setLogoFailed(true)}
                    />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full grid place-items-center font-bold text-sm shadow-md"
                       style={{ background: accent, color: olive }}>
                    {(partner?.name || "R").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div>
                  <div
                    className="font-bold text-[15px] leading-tight tracking-tight"
                    style={{ color: olive, fontFamily: '"Outfit", sans-serif' }}
                    data-testid="footer-brand"
                  >
                    {brand}
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase font-semibold mt-0.5" style={{ color: accent }}>
                    {subtitle}
                  </div>
                </div>
              </>
            )}
          </div>
          <p className="text-sm max-w-md leading-relaxed mt-4" style={{ color: oliveSoft }}>{desc}</p>
        </div>
        <div>
          <div className="overline mb-3 text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: olive }}>{f.product}</div>
          <ul className="space-y-2 text-sm" style={{ color: oliveSoft }}>
            <li><Link to={`${prefix}/configurator`} className="footer-link" style={{ color: oliveSoft }}>{t.nav.configurator}</Link></li>
            <li><Link to={`${prefix}/catalog`} className="footer-link" style={{ color: oliveSoft }}>{t.nav.catalog}</Link></li>
            <li><Link to={`${prefix}/bundles`} className="footer-link" style={{ color: oliveSoft }}>{t.nav.bundles}</Link></li>
          </ul>
        </div>
        <div>
          <div className="overline mb-3 text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: olive }}>{f.account}</div>
          <ul className="space-y-2 text-sm" style={{ color: oliveSoft }}>
            <li><Link to={`${prefix}/login`} className="footer-link" style={{ color: oliveSoft }}>{t.nav.login}</Link></li>
            <li><Link to={`${prefix}/register`} className="footer-link" style={{ color: oliveSoft }}>{t.nav.register}</Link></li>
            <li><Link to={`${prefix}/account`} className="footer-link" style={{ color: oliveSoft }}>{t.nav.account}</Link></li>
          </ul>
        </div>
      </div>
      <div className="container-x border-t py-6 text-xs" style={{ borderColor: "#E5DFC8", color: oliveMuted }}>
        © {new Date().getFullYear()} {brand} · {t.footer}
      </div>
    </footer>
  );
}
